import type { BodyId, BodyState } from '../core/types';
import { maxApoapsis } from '../core/orbit-geometry';

/**
 * Session-only VIEW state (Story 3.2). The camera is a MODIFIER folded over the
 * always-recomputed fit baseline at ONE render site (`composeCamera` in
 * `render/projection.ts`); `{ zoom: 1, panX: 0, panY: 0 }` is exactly fit-all, so
 * "Fit all orbits" is the identity modifier.
 *
 * `zoom` is a unitless multiplier over the fit baseline (`1` = fit-all). `panX`/
 * `panY` ARE the projection's `worldCentre` (SI world metres, focus at origin):
 * the world point shown at the viewport centre. The store stays viewport-free
 * (AD-1/AD-2) — the fit baseline needs the viewport, so it is folded in only at
 * render time; the reducer's clamps/steps use only `state` data.
 *
 * Camera is EXCLUDED from the shareable-config set (AD-4 excludes "zoom/pan"). It
 * is seeded in `createDefaultState()`, so `Reset` reframes to fit-all (like
 * `phaseOffsets`). All helpers are PURE: no DOM, no viewport, no `Date`; a
 * non-finite input returns the input unchanged (non-blocking convention, mirrors
 * `NudgePhase`/`SetSpeed`).
 */
export interface ViewCamera {
  /** Zoom multiplier over the fit baseline. `1` = fit-all. */
  readonly zoom: number;
  /** World x (metres) shown at the viewport centre (pan). */
  readonly panX: number;
  /** World y (metres) shown at the viewport centre (pan). */
  readonly panY: number;
}

/** The identity modifier: fit-all. `FitAllOrbits` (the Fit button) resets to this. */
export const FIT_VIEW_CAMERA: ViewCamera = { zoom: 1, panX: 0, panY: 0 };

/**
 * The BOOT / `Reset` framing (Story 3.2 follow-up, revised Story 3.4). Fitting
 * all eight orbits at once (`FIT_VIEW_CAMERA`) crushes the inner system: at
 * fit-all the four inner planets project to within ~20px of the focus, so they
 * pile onto the Sun. Real solar-system sims avoid this by NOT opening at
 * fit-all. We seed a zoom that frames roughly out to Jupiter, so the inner
 * planets read clearly on load, while the Fit button still zooms out to every
 * orbit and Reset returns here.
 *
 * `zoom` is a multiplier over the fit baseline (which frames the outermost
 * apoapsis). Story 3.4 found that the previous value, 5.5, left Mercury's
 * marker overlapping the Sun's marker by ~0.5px at Mercury's periapsis (the
 * boot worst case, 1440x900 dpr2): the two readable markers need the
 * Sun/Mercury screen separation to exceed zoom > ~5.57 before they clear. `6`
 * is the smallest round step past that threshold (clears by ~3px) while still
 * framing roughly the inner system out toward Jupiter's apoapsis. Distances
 * stay honest and linear: this only changes the initial framing, never the
 * geometry. Centred on the focus (`pan = 0`), so the Sun stays centred. This is
 * a session-only view seed (excluded from the shareable-config set, see the
 * module doc above); the coupling to the default reference data that makes `6`
 * correct is asserted in `tests/unit/boot-framing.test.ts`, not here (AD-1:
 * this file stays a pure constant with no reference-data import).
 */
export const DEFAULT_VIEW_CAMERA: ViewCamera = { zoom: 6, panX: 0, panY: 0 };

/** Zoom multiplier bounds (AC-4: keep the view finite and recoverable). */
export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 32;

/** Discrete pan step, as a fraction of the visible world extent per step. */
export const PAN_STEP_FRACTION = 0.15;

/** Pan is bounded to +/- PAN_LIMIT_FACTOR * maxApoapsis (finite/recoverable). */
export const PAN_LIMIT_FACTOR = 4;

/**
 * Discrete zoom factor for the keyboard `+`/`-` keys and the top-bar Zoom In/Out
 * buttons. Shared by `ui` so button and key zoom cannot diverge.
 */
export const ZOOM_STEP = 1.3;

/**
 * Finite fallback pan bound (metres) used when `maxApoapsis` is 0 (no active
 * orbiter). ~1 AU: keeps the pan clamp finite so AC-4 holds even in a degenerate
 * single-body view. Not a display literal (AD-5): it is an SI metre guard rail.
 */
export const FALLBACK_MAX_APOAPSIS_METRES = 1.495978707e11;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Zoom about an optional focal world point. `factor > 1` zooms in. When a focus
 * `(focusX, focusY)` is supplied (wheel/pinch), the pan is adjusted so that world
 * point keeps the same SCREEN pixel across the zoom; omitting it (keyboard/button)
 * leaves the pan unchanged (zoom about the viewport centre). The pan-adjust uses
 * the ACTUAL applied ratio after clamping (`zoom_next / zoom`), so it is exact at
 * the clamp bounds and independent of `metresPerCssPixel`/the viewport.
 */
export function applyZoom(
  view: ViewCamera,
  factor: number,
  focusX?: number,
  focusY?: number
): ViewCamera {
  if (!Number.isFinite(factor) || factor <= 0) return view;
  const zoomNext = clamp(view.zoom * factor, MIN_ZOOM, MAX_ZOOM);
  const effFactor = zoomNext / view.zoom;
  if (
    focusX === undefined ||
    focusY === undefined ||
    !Number.isFinite(focusX) ||
    !Number.isFinite(focusY)
  ) {
    return { zoom: zoomNext, panX: view.panX, panY: view.panY };
  }
  const panX = focusX - (focusX - view.panX) / effFactor;
  const panY = focusY - (focusY - view.panY) / effFactor;
  return { zoom: zoomNext, panX, panY };
}

/**
 * Continuous world-metre pan (from the projection-bearing pointer drag). Additive,
 * then clamped to +/- PAN_LIMIT_FACTOR * maxApoapsis so the view stays finite and
 * recoverable (AC-4). A non-finite delta returns the input unchanged.
 */
export function applyPanWorld(
  view: ViewCamera,
  dxWorld: number,
  dyWorld: number,
  maxApoapsisMetres: number
): ViewCamera {
  if (!Number.isFinite(dxWorld) || !Number.isFinite(dyWorld)) return view;
  const basis =
    maxApoapsisMetres > 0 ? maxApoapsisMetres : FALLBACK_MAX_APOAPSIS_METRES;
  const limit = PAN_LIMIT_FACTOR * basis;
  return {
    zoom: view.zoom,
    panX: clamp(view.panX + dxWorld, -limit, limit),
    panY: clamp(view.panY + dyWorld, -limit, limit)
  };
}

/**
 * Discrete, zoom-aware pan step (top-bar directional buttons and canvas arrow
 * keys — neither carries a projection). The step is `PAN_STEP_FRACTION` of the
 * currently framed world span (`maxApoapsis / zoom`), so a step feels like the
 * same fraction of the visible view at any zoom. Screen-up maps to world +y.
 */
export function applyPanStep(
  view: ViewCamera,
  dir: 'up' | 'down' | 'left' | 'right',
  maxApoapsisMetres: number
): ViewCamera {
  const basis =
    maxApoapsisMetres > 0 ? maxApoapsisMetres : FALLBACK_MAX_APOAPSIS_METRES;
  const worldStep = PAN_STEP_FRACTION * (basis / view.zoom);
  let dx = 0;
  let dy = 0;
  switch (dir) {
    case 'left':
      dx = -worldStep;
      break;
    case 'right':
      dx = worldStep;
      break;
    case 'up':
      dy = worldStep;
      break;
    case 'down':
      dy = -worldStep;
      break;
  }
  return applyPanWorld(view, dx, dy, maxApoapsisMetres);
}

/**
 * The clamp basis for the reducer: the largest apoapsis among the active,
 * non-central orbiters, using the IDENTICAL filter both renderer adapters use
 * (`b.id !== centralBody && b.status === 'active'`) so the reducer's clamp basis
 * and the render fit basis cannot diverge. Live `{ a, e }` come straight from the
 * store bodies; returns 0 when no orbiter qualifies (the helpers then fall back to
 * `FALLBACK_MAX_APOAPSIS_METRES`).
 */
export function maxApoapsisMetresForState(
  bodies: readonly BodyState[],
  centralBody: BodyId
): number {
  const orbiting = bodies
    .filter((b) => b.id !== centralBody && b.status === 'active')
    .map((b) => ({ a: b.a, e: b.e }));
  return maxApoapsis(orbiting);
}
